import { useImperativeHandle, useRef } from 'react';
import { useTheme } from '../../../../../theme/theme';

export const useUploadProgress = ({ ref }) => {
  const progressValueRef = useRef<HTMLDivElement>(null);
  const progressLabelRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const progressIconRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();

  const removeProgressIconInteractivity = () => {
    if (progressIconRef.current) {
      progressIconRef.current.onclick = null;
      progressIconRef.current.onkeydown = null;
      progressIconRef.current.tabIndex = -1;
    }
  };

  useImperativeHandle(ref, () => ({
    bindOnCancel(controller) {
      if (progressIconRef.current) {
        progressIconRef.current.onclick = () => {
          controller.abort();
        };

        progressIconRef.current.onkeydown = evt => {
          if (evt.key === ' ' || evt.key === 'Enter') {
            controller.abort();
          }
        };
      }
    },

    onUploadProgress(p: any) {
      const progress = Math.ceil((p.loaded / p.total) * 100);

      if (progressValueRef.current && progressBarRef.current) {
        progressValueRef.current.innerText = `${progress}%`;
        progressBarRef.current.style.width = `${progress}%`;
      }
    },

    onUploadSuccess() {
      if (
        progressLabelRef.current &&
        progressBarRef.current &&
        progressValueRef.current &&
        progressIconRef.current
      ) {
        progressLabelRef.current.innerText = 'Completed';
        progressValueRef.current.innerText = '100%';
        progressBarRef.current.style.width = '100%';
        progressBarRef.current.style.background = theme.colors.success['500'];
        progressIconRef.current.className = 'fas fa-check-circle';
        progressIconRef.current.style.color = theme.colors.success['500'];
        removeProgressIconInteractivity();
      }
    },

    onCancel() {
      if (
        progressLabelRef.current &&
        progressBarRef.current &&
        progressValueRef.current &&
        progressIconRef.current
      ) {
        progressLabelRef.current.innerText = 'Upload Cancelled';
        progressValueRef.current.innerText = '';
        progressBarRef.current.style.width = '100%';
        progressBarRef.current.style.background = theme.colors.neutral['400'];
        progressIconRef.current.style.display = 'none';
        removeProgressIconInteractivity();
      }
    },

    onError() {
      if (
        progressLabelRef.current &&
        progressBarRef.current &&
        progressValueRef.current &&
        progressIconRef.current
      ) {
        progressLabelRef.current.innerText = 'Upload Error';
        progressValueRef.current.innerText = '';
        progressBarRef.current.style.width = '100%';
        progressBarRef.current.style.background = theme.colors.error['500'];
        progressIconRef.current.className = 'fas fa-times-octagon';
        progressIconRef.current.style.color = theme.colors.error['500'];
        removeProgressIconInteractivity();
      }
    },
  }));

  return {
    progressValueRef,
    progressLabelRef,
    progressBarRef,
    progressIconRef,
  };
};
